<template>
  <div
    id="app"
    ref="appRef"
    class="sf-app"
    :class="{
      'native-app': isNativeApp,
      'sf-safari': isSafari,
      'sf-translated': currentLang !== 'en',
      [`sf-lang-${currentLang}`]: true,
    }"
    @scroll="onScroll"
  >
    <div id="observer-screen-top-target"></div>
    <div id="iosFix"></div>

    <AppNotificationContainer />
    <CompletionPopupHost />
    <MiniPlayer v-if="showMiniPlayer" />

    <RouterView v-slot="{ Component }">
      <KeepAlive
        :max="keepAliveComponents.length"
        :include="keepAliveComponents"
      >
        <component :is="Component" :key="componentKey" />
      </KeepAlive>
    </RouterView>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { emitter } from '@/services/mitt'
import { platform } from '@/services/platform'
import { isSafari as _isSafari } from '@/helpers/userAgent'
import { getProperLanguage, weglotInitialized } from '@/services/weglot'
import { backLink } from '@/models/backlink'
import {
  miniPlayer19040Variant,
  onboardingChecklist19055Variant,
} from '@/services/ab'
import { resumeChecklist } from '@/models/checklist'
import { playerSession, resumePlayerSession } from '@/services/player/session'
import { startNativeProgressSync } from '@/services/player/native-progress'

import AppNotificationContainer from '@/app/components/AppNotificationContainer.vue'
import CompletionPopupHost from '@/app/components/completion/CompletionPopupHost.vue'
import MiniPlayer from '@/app/components/player/MiniPlayer.vue'

const route = useRoute()

const appRef = ref<HTMLElement | null>(null)

const isNativeApp = ref(false)
const isSafari = ref(false)
const currentLang = ref('en')

onBeforeMount(async () => {
  isNativeApp.value = await platform.isNativeApp()
  isSafari.value = (await platform.isIOS()) || _isSafari()
  // Subscribe to audio progress accumulated by iOS native audio playback plugin
  // to sync it with javascript state
  startNativeProgressSync()

  if (weglotInitialized()) {
    listenForLanguageChanges()
  } else {
    window.addEventListener('weglot_initialized', () => {
      listenForLanguageChanges()
    })
  }

  // If the user has already started the checklist experiment,
  // we initialize the checklist with their stored progress.
  if (onboardingChecklist19055Variant() === 'B') {
    await resumeChecklist()
  }

  // Load the last doc the user viewed into the Mini Player.
  if (miniPlayer19040Variant() === 'B') {
    await resumePlayerSession()
  }
})

/**
 * Show the Mini Player when not in a doc content page.
 */
const showMiniPlayer = computed(() => {
  return (
    playerSession.value.canShowMiniPlayer &&
    playerSession.value.doc &&
    !route.meta?.isDocPage
  )
})

/**
 * A list of components from the navigation history that we
 * want to cache with `KeepAlive`.
 *
 * This list is dynamic instead of static because we can't manually
 * destroy cached components, but `KeepAlive` will destroy any caches
 * from components that are not in its `include` list, so by changing
 * the contents of this list, we can effectively control which caches
 * to destroy and which to keep.
 */
const keepAliveComponents = computed(() => {
  return backLink.value.keepAliveComponents
})

/**
 * Create a key for components inside `KeepAlive`.
 *
 * This is used to differentiate routes that use the
 * same component. Without it, they would share the
 * same cache.
 */
const componentKey = computed(() => {
  if (!route.meta?.keepAlive) {
    return
  }

  return route.path
})

/**
 * Change the value of `currentLang` whenever
 * the current language changes.
 */
function listenForLanguageChanges(): void {
  const weglot = (window as any).Weglot

  currentLang.value = getProperLanguage(weglot.getCurrentLang())

  weglot.on('languageChanged', (newLang: string) => {
    currentLang.value = newLang
  })
}

function resizeHeight(): void {
  // On mobile browsers, the address bar can reduce the viewport
  // height without reducing the vh unit.
  // So if we set the height to 100vh, the bottom may be obscured by
  // an address bar or navbar in the app.
  //
  // To work around this, we set the CSS variable --vh to 1/100
  // of the height of the actual viewport, and then set this
  // instead of 100vh:
  //   height: calc(var(--vh, 1vh) * 100);
  // More: https://chanind.github.io/javascript/2019/09/28/avoid-100vh-on-mobile-web.html
  const windowsVH = window.innerHeight / 100
  appRef.value?.style.setProperty('--vh', windowsVH + 'px')
}

onMounted(() => {
  resizeHeight()
  window.addEventListener('resize', () => {
    resizeHeight()
    emitResizeEvent()
  })

  window.addEventListener('orientationchange', () => {
    resizeHeight()
    emitResizeEvent()
  })
})

function emitResizeEvent(): void {
  emitter.emit('windowResize')
}

function onScroll(): void {
  emitter.emit('appScroll', appRef.value as HTMLElement)
}
</script>

<style lang="scss">
/*
 * This element is used to top determine wheter or not we are at the top of the page.
 * It has a height of x and margin-bottom of -x, so that it stays in the regular document flow,
 * but is not visible.
 */
#observer-screen-top-target {
  pointer-events: none;
  visibility: hidden;
  height: 30px;
  margin-bottom: -30px;
}
</style>
